import React, { useState, useMemo, useEffect } from "react";
import {
  ToolShell, Field, UnitNumber, Select, Result,
  toBase, fromBase, fmt,
  SpecCard, SpecValue,
} from "./_shared";

// L/p/day defaults for the FULL-TIME population (guests, residents — bathe,
// sleep, eat on site).  Editable on every profile.
const PROFILES = [
  { value: "luxury",     label: "Luxury 5★ lodge / villa",          lpd: 650 },
  { value: "boutique",   label: "Boutique 4★ hotel / lodge",        lpd: 400 },
  { value: "midtier",    label: "Mid-tier 3★ hospitality",          lpd: 300 },
  { value: "eco",        label: "Eco lodge / tented camp",          lpd: 200 },
  { value: "residential",label: "Residential home",                  lpd: 250 },
  { value: "estate",     label: "Estate / sectional title",          lpd: 220 },
  { value: "staff",      label: "Staff village (live-in)",            lpd: 100 },
  { value: "agri",       label: "Agricultural site / temporary camp", lpd: 50 },
  { value: "office",     label: "Office (no live-in)",                lpd: 25 },
  { value: "school",     label: "School (day pupils)",                lpd: 25 },
  { value: "restaurant", label: "Restaurant guest (per cover)",       lpd: 25 },
  { value: "conference", label: "Conference delegate (day)",          lpd: 30 },
  { value: "hospital",   label: "Hospital bed",                       lpd: 600 },
  { value: "custom",     label: "Custom — enter L/p/day below",       lpd: null },
];
// Risk profile presets — each risk band ships a suggested split between
// "clean days" (buffer if the plant / feed side fails) and "raw days"
// (buffer if the source fails but the plant is still running). Both are
// editable — this is just a starting point per site type.
const RISK_PROFILES = [
  { value: "1", label: "1 day — metro site with reliable backup",       cleanDays: 0.5, rawDays: 0.5 },
  { value: "2", label: "2 days — peri-urban / reticulated municipal",   cleanDays: 1,   rawDays: 1   },
  { value: "3", label: "3 days — semi-remote / single source",          cleanDays: 1.5, rawDays: 1.5 },
  { value: "5", label: "5 days — remote / off-grid",                    cleanDays: 2,   rawDays: 3   },
  { value: "7", label: "7 days — critical site, no alternative supply", cleanDays: 3,   rawDays: 4   },
];

// Same pattern as Purification Plant Sizing — pick between deriving daily
// demand from head-count × L/p/day, or entering a known daily volume directly.
const DEMAND_MODES = [
  { value: "people", label: "Derive from people × L/p/day (full + part time)" },
  { value: "direct", label: "I already know the daily volume" },
];

export default function StorageSizingCalculator() {
  // Profile
  const [profile, setProfile] = useState("luxury");
  const [fullTimeLpd, setFullTimeLpd] = useState(650);   // guests / residents
  const [partTimeLpd, setPartTimeLpd] = useState(50);    // day staff / commuters

  // Head-count, split full-time / part-time
  const [fullTimePersons, setFullTimePersons] = useState(40);
  const [partTimePersons, setPartTimePersons] = useState(20);
  const [occupancyPct, setOccupancyPct] = useState(70);

  // Risk + irrigation
  //  riskDays  — preset dropdown; when it changes we push its suggested
  //              split into cleanDays / rawDays.  Both remain user-editable.
  //  cleanDays — days of treated water held if plant / feed fails.
  //  rawDays   — days of raw water held if source fails but plant still runs.
  const [riskDays, setRiskDays] = useState("3");
  const [cleanDays, setCleanDays] = useState(1.5);
  const [rawDays, setRawDays] = useState(1.5);
  const [includeIrrigation, setIncludeIrrigation] = useState(false);
  const [irrVal, setIrrVal] = useState(2000);
  const [irrUnit, setIrrUnit] = useState("L");

  // Site supply rates — drives the raw-vs-clean reserve split
  const [rawSupply, setRawSupply] = useState(15000);     // L/day from borehole / municipal
  const [cleanSupply, setCleanSupply] = useState(10000); // L/day production from treatment

  // Demand input mode — same UX as Purification Plant Sizing.
  // "people"  → derive from head-count × L/p/day × occupancy (default)
  // "direct"  → user already knows their daily potable volume (L/day)
  const [demandMode, setDemandMode] = useState("people");
  const [dailyL, setDailyL] = useState(20000);

  // Apply profile default for full-time L/p/day (part-time stays user-controlled)
  useEffect(() => {
    const p = PROFILES.find((x) => x.value === profile);
    if (p && p.lpd != null) setFullTimeLpd(p.lpd);
  }, [profile]);

  // Push the risk-profile suggested clean/raw split into the editable fields.
  useEffect(() => {
    const r = RISK_PROFILES.find((x) => x.value === riskDays);
    if (r) { setCleanDays(r.cleanDays); setRawDays(r.rawDays); }
  }, [riskDays]);

  const occ = occupancyPct / 100;
  const totalDays = cleanDays + rawDays;

  // ───────── daily demand ─────────
  const fullTimeDaily = fullTimePersons * fullTimeLpd * occ;
  const partTimeDaily = partTimePersons * partTimeLpd * occ;
  const peopleDaily_L = fullTimeDaily + partTimeDaily;
  const potableDaily_L = demandMode === "direct" ? dailyL : peopleDaily_L;
  const irrDaily_L = includeIrrigation ? toBase("volume", irrVal, irrUnit) : 0;
  const dailyTotal_L = potableDaily_L + irrDaily_L;
  // ───────── storage sizing ─────────
  //
  //   Clean water tank  = D × cleanDays  →  days of water at the tap if the
  //                                          plant / feed side fails.
  //   Raw   water tank  = D × rawDays    →  days of raw water available to
  //                                          feed the plant if the source
  //                                          (borehole / municipal) fails
  //                                          while the plant still runs.
  //   Total site storage = clean + raw.
  //
  //   The two-way split matters — clean-only protects against plant failure
  //   but leaves zero margin for a source outage; raw-only protects the
  //   plant but leaves the tap dry the moment the plant hiccups.  A real
  //   site sizes both. Risk-profile presets suggest a starting split; the
  //   engineer edits either day-count to suit the site.
  //
  const cleanReserve_L = dailyTotal_L * cleanDays;
  const rawReserve_L   = dailyTotal_L * rawDays;
  const totalStorage_L = cleanReserve_L + rawReserve_L;

  // Steady-state supply flags (independent of tank sizing).
  const sourceIsBottleneck = rawSupply   > 0 && rawSupply   < dailyTotal_L;
  const plantIsBottleneck  = cleanSupply > 0 && cleanSupply < dailyTotal_L;

  const supplyStatus = plantIsBottleneck ? "alert"
                     : sourceIsBottleneck ? "warn"
                     : "ok";
  const supplyNote = plantIsBottleneck
    ? `Plant capacity ${fmt(cleanSupply, 0)} L/day is below daily demand of ${fmt(dailyTotal_L, 0)} L/day. Storage cannot compensate for an undersized plant — resize the treatment train.`
    : sourceIsBottleneck
    ? `Raw source ${fmt(rawSupply, 0)} L/day is below daily demand — steady-state clean production is capped at the source rate. Raw buffer of ${fmt(rawDays, 1)} day${rawDays === 1 ? "" : "s"} covers short source interruptions; long outages need tankering.`
    : `Raw source ${fmt(rawSupply, 0)} L/day ≥ demand. Plant supplies ${fmt((cleanSupply / dailyTotal_L), 2)}× daily demand — healthy margin.`;

  return (
    <ToolShell
      slug="storage-sizing"
      title="Water storage sizing"
      oneLiner="Daily demand plus a split buffer — clean-water days (if the plant / feed fails) and raw-water days (if the source fails). Risk-profile presets suggest a split; edit either day-count to match this site."
      onExport={() => ({
        inputs: [
          { label: "Demand input method",
            value: demandMode === "direct"
              ? "Direct — user-supplied daily volume"
              : "Derived — people × L/p/day (full + part time)" },
          ...(demandMode === "direct"
            ? [{ label: "Daily volume (potable)", value: fmt(dailyL, 0), unit: "L/day" }]
            : [
                { label: "Site profile", value: PROFILES.find((p) => p.value === profile)?.label ?? profile },
                { label: "Full-time persons (guests / residents)", value: fullTimePersons, unit: "persons" },
                { label: "Full-time L/p/day", value: fullTimeLpd, unit: "L/p/day" },
                { label: "Part-time persons (day staff / commuters)", value: partTimePersons, unit: "persons" },
                { label: "Part-time L/p/day", value: partTimeLpd, unit: "L/p/day" },
                { label: "Average occupancy", value: occupancyPct, unit: "%" },
              ]),
          { label: "Risk profile (preset)", value: RISK_PROFILES.find((r) => r.value === riskDays)?.label ?? `${totalDays} days` },
          { label: "Clean water buffer days", value: fmt(cleanDays, 1), unit: cleanDays === 1 ? "day" : "days" },
          { label: "Raw water buffer days",   value: fmt(rawDays, 1),   unit: rawDays === 1   ? "day" : "days" },
          ...(includeIrrigation ? [{ label: "Irrigation / landscape per day", value: irrVal, unit: irrUnit }] : []),
          { label: "Raw water available per day", value: rawSupply, unit: "L/day" },
          { label: "Purified water available per day", value: cleanSupply, unit: "L/day" },
        ],
        results: [
          { label: "Daily demand", value: fmt(dailyTotal_L, 0), unit: "L / day",
            alts: [
              { value: fmt(fromBase("volume", dailyTotal_L, "kL"), 1), unit: "kL" },
              { value: fmt(fromBase("volume", dailyTotal_L, "m3"), 1), unit: "m³" },
            ] },
          { label: "Clean water tank", value: fmt(cleanReserve_L, 0), unit: "L",
            note: `${fmt(cleanDays, 1)} day${cleanDays === 1 ? "" : "s"} of treated water held at the tap if the plant / feed stops.` },
          { label: "Raw water tank", value: fmt(rawReserve_L, 0), unit: "L",
            note: `${fmt(rawDays, 1)} day${rawDays === 1 ? "" : "s"} of raw water held to feed the plant if the source stops.` },
          { label: "Total site storage", value: fmt(totalStorage_L, 0), unit: "L",
            note: `Clean ${fmt(cleanReserve_L / 1000, 1)} kL + raw ${fmt(rawReserve_L / 1000, 1)} kL over ${fmt(totalDays, 1)} combined day${totalDays === 1 ? "" : "s"}.` },
          { label: "Supply status",
            value: plantIsBottleneck   ? "Plant undersized"
                 : sourceIsBottleneck  ? "Source below demand"
                 : `${fmt((cleanSupply / dailyTotal_L), 2)}× plant margin`,
            status: supplyStatus, note: supplyNote },
        ],
        notes: [
          "Daily demand = (full-time × L/p/day + part-time × L/p/day) × occupancy + irrigation (or direct entry).",
          "Clean water tank = daily demand × clean buffer days — days of supply at the tap if the plant / feed stops.",
          "Raw water tank = daily demand × raw buffer days — days of raw water to keep the plant fed if the source stops.",
          "Risk-profile presets suggest a split; edit either day-count to match this site.",
        ],
        spec: {
          title: "Storage specification",
          body: demandMode === "direct"
            ? `This site needs <strong>${fmt(dailyTotal_L, 0)} L/day</strong> of treated water (direct entry${includeIrrigation ? ` + ${fmt(irrDaily_L, 0)} L/day irrigation` : ""}). Specify <strong>${fmt(cleanReserve_L / 1000, 1)} kL</strong> of clean water storage (${fmt(cleanDays, 1)}-day buffer if the plant / feed fails) and <strong>${fmt(rawReserve_L / 1000, 1)} kL</strong> of raw water storage (${fmt(rawDays, 1)}-day buffer if the source fails). Total site storage: <strong>${fmt(totalStorage_L / 1000, 1)} kL</strong>.${sourceIsBottleneck ? ` Raw source at <strong>${fmt(rawSupply, 0)} L/day</strong> is below demand — steady-state clean production is capped at the source rate; the raw buffer covers short source interruptions only.` : ""}${plantIsBottleneck ? ` <strong>Plant capacity ${fmt(cleanSupply, 0)} L/day is below demand</strong> — resize the treatment train before finalising storage.` : ""}`
            : `A ${PROFILES.find((p) => p.value === profile)?.label?.toLowerCase() ?? "site"} with <strong>${fullTimePersons} full-time</strong> and <strong>${partTimePersons} part-time</strong> persons at <strong>${occupancyPct} %</strong> occupancy needs <strong>${fmt(dailyTotal_L, 0)} L/day</strong>. Specify <strong>${fmt(cleanReserve_L / 1000, 1)} kL</strong> of clean water storage (${fmt(cleanDays, 1)}-day buffer if the plant / feed fails) and <strong>${fmt(rawReserve_L / 1000, 1)} kL</strong> of raw water storage (${fmt(rawDays, 1)}-day buffer if the source fails). Total site storage: <strong>${fmt(totalStorage_L / 1000, 1)} kL</strong>.${sourceIsBottleneck ? ` Raw source at <strong>${fmt(rawSupply, 0)} L/day</strong> is below demand — steady-state clean production is capped at the source rate; the raw buffer covers short source interruptions only.` : ""}${plantIsBottleneck ? ` <strong>Plant capacity ${fmt(cleanSupply, 0)} L/day is below demand</strong> — resize the treatment train before finalising storage.` : ""}`,
        },
      })}
    >
      <div className="grid md:grid-cols-2 gap-5">
        {/* Demand input method — same UX as the Purification Plant Sizing tool. */}
        <Field label="Input method" span={2}>
          <Select value={demandMode} onChange={setDemandMode} options={DEMAND_MODES} testid="ss-demand-mode" />
        </Field>

        {/* Direct daily-volume input (shown when the user already knows demand). */}
        {demandMode === "direct" && (
          <Field
            label="Daily volume"
            span={2}
            hint="Total potable / treated water needed per day."
          >
            <PlainSuffix value={dailyL} onChange={setDailyL} suffix="L/day" testid="ss-daily-direct" />
          </Field>
        )}

        {/* People-based derivation (default). */}
        {demandMode === "people" && (
          <>
            <Field label="Site profile" span={2}>
              <Select value={profile} onChange={setProfile} options={PROFILES} testid="ss-profile" />
            </Field>

            <Field
              label="Full-time persons"
              hint="Guests, residents and live-in staff — bathe, shower, sleep and eat on site."
            >
              <PlainSuffix value={fullTimePersons} onChange={setFullTimePersons} suffix="persons" testid="ss-ft-persons" />
            </Field>
            <Field
              label="Full-time L/p/day"
              hint="Pre-loaded from the profile · over-write for your site."
            >
              <PlainSuffix value={fullTimeLpd} onChange={setFullTimeLpd} suffix="L/p/day" testid="ss-ft-lpd" />
            </Field>

            <Field
              label="Part-time persons"
              hint="Day staff, commuters, drop-in workers — do not bathe / shower on site."
            >
              <PlainSuffix value={partTimePersons} onChange={setPartTimePersons} suffix="persons" testid="ss-pt-persons" />
            </Field>
            <Field
              label="Part-time L/p/day"
              hint="Toilet, hand-wash, drinking water only — typically 30–50 L/p/day."
            >
              <PlainSuffix value={partTimeLpd} onChange={setPartTimeLpd} suffix="L/p/day" testid="ss-pt-lpd" />
            </Field>

            <Field label="Average occupancy" span={2} hint="100 % if permanent, lower for seasonal sites.">
              <PlainSuffix value={occupancyPct} onChange={setOccupancyPct} suffix="%" testid="ss-occ" />
            </Field>
          </>
        )}

        {/* Daily potable demand summary (mirrors Plant Sizing's permeate target). */}
        <div
          className="md:col-span-2 mt-1 px-4 py-2"
          style={{ background: "#FCFCFA", border: "1px solid rgba(12,42,64,0.08)" }}
          data-testid="ss-effective-demand"
        >
          <span
            className="font-mono uppercase"
            style={{ fontSize: "0.58rem", letterSpacing: "0.2em", color: "rgba(12,42,64,0.55)" }}
          >
            Daily potable demand
          </span>
          <span className="ml-3" style={{ fontSize: "1.05rem", fontWeight: 700, color: "#0C2A40" }}>
            {fmt(potableDaily_L, 0)} L/day
          </span>
          <span className="ml-2 font-mono" style={{ fontSize: "0.72rem", color: "rgba(12,42,64,0.55)" }}>
            = {fmt(potableDaily_L / 1000, 2)} m³/day
          </span>
          {demandMode === "people" && (
            <span className="ml-3 font-light" style={{ fontSize: "0.78rem", color: "rgba(12,42,64,0.6)" }}>
              (full-time {fmt(fullTimeDaily, 0)} + part-time {fmt(partTimeDaily, 0)} L/day)
            </span>
          )}
        </div>

        <Field
          label="Risk profile (preset)"
          span={2}
          hint="Sets suggested clean and raw buffer days for this site type. Both are editable below."
        >
          <Select value={riskDays} onChange={setRiskDays} options={RISK_PROFILES} testid="ss-risk" />
        </Field>

        <Field
          label="Clean water buffer"
          hint="Days of treated water held at the tap if the plant / feed stops."
        >
          <PlainSuffix value={cleanDays} onChange={setCleanDays} suffix={cleanDays === 1 ? "day" : "days"} testid="ss-clean-days" />
        </Field>
        <Field
          label="Raw water buffer"
          hint="Days of raw water held to keep the plant fed if the source stops."
        >
          <PlainSuffix value={rawDays} onChange={setRawDays} suffix={rawDays === 1 ? "day" : "days"} testid="ss-raw-days" />
        </Field>

        {/* Combined buffer summary. */}
        <div
          className="md:col-span-2 -mt-2 px-4 py-2"
          style={{ background: "#FCFCFA", border: "1px solid rgba(12,42,64,0.08)" }}
          data-testid="ss-total-days"
        >
          <span
            className="font-mono uppercase"
            style={{ fontSize: "0.58rem", letterSpacing: "0.2em", color: "rgba(12,42,64,0.55)" }}
          >
            Combined buffer
          </span>
          <span className="ml-3" style={{ fontSize: "1.05rem", fontWeight: 700, color: "#0C2A40" }}>
            {fmt(totalDays, 1)} day{totalDays === 1 ? "" : "s"}
          </span>
          <span className="ml-2 font-mono" style={{ fontSize: "0.72rem", color: "rgba(12,42,64,0.55)" }}>
            = {fmt(cleanDays, 1)} clean + {fmt(rawDays, 1)} raw
          </span>
        </div>

        {/* Irrigation per day */}
        <Field
          label="Irrigation / landscape load per day"
          span={2}
          hint="Optional · daily litres for garden, turf and water features."
        >
          <div className="flex items-center gap-3 mt-1">
            <button type="button" onClick={() => setIncludeIrrigation(!includeIrrigation)}
              data-testid="ss-irr-toggle"
              className="px-4 py-2 border font-mono"
              style={{
                background: includeIrrigation ? "#1FB6C1" : "#FFFFFF",
                color: includeIrrigation ? "#FFFFFF" : "#0C2A40",
                borderColor: includeIrrigation ? "#1FB6C1" : "rgba(12,42,64,0.16)",
                fontSize: "0.7rem", letterSpacing: "0.1em", textTransform: "uppercase",
              }}>
              {includeIrrigation ? "Included" : "Add"}
            </button>
            {includeIrrigation && (
              <div className="flex-1">
                <UnitNumber dim="volume" value={irrVal} onChange={setIrrVal} unit={irrUnit} onUnitChange={setIrrUnit}
                  units={["L", "kL", "m3"]} testid="ss-irr-l" />
              </div>
            )}
          </div>
        </Field>

        {/* Bottleneck inputs */}
        <Field
          label="Raw water available per day"
          hint="What your borehole, municipal feed or harvested source can deliver per day."
        >
          <PlainSuffix value={rawSupply} onChange={setRawSupply} suffix="L/day" testid="ss-raw" />
        </Field>
        <Field
          label="Purified water available per day"
          hint="What your treatment train can produce per day at the duty point."
        >
          <PlainSuffix value={cleanSupply} onChange={setCleanSupply} suffix="L/day" testid="ss-clean" />
        </Field>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
        <Result
          label="Daily demand"
          primary={{ value: fmt(dailyTotal_L, 0), unit: "L / day" }}
          alts={[
            { value: fmt(fromBase("volume", dailyTotal_L, "kL"), 1), unit: "kL" },
            { value: fmt(fromBase("volume", dailyTotal_L, "m3"), 1), unit: "m³" },
          ]}
          note={demandMode === "direct"
            ? `User-supplied daily volume${includeIrrigation ? ` + irrigation ${fmt(irrDaily_L, 0)}` : ""} L/day`
            : `Full-time ${fmt(fullTimeDaily, 0)} + part-time ${fmt(partTimeDaily, 0)}${includeIrrigation ? ` + irrigation ${fmt(irrDaily_L, 0)}` : ""} L/day`}
          testid="ss-result-daily"
        />
        <Result
          label="Clean water reserve"
          primary={{ value: fmt(cleanReserve_L, 0), unit: "L" }}
          alts={[
            { value: fmt(fromBase("volume", cleanReserve_L, "kL"), 1), unit: "kL" },
            { value: fmt(fromBase("volume", cleanReserve_L, "m3"), 1), unit: "m³" },
          ]}
          note={`${fmt(cleanDays, 1)} day${cleanDays === 1 ? "" : "s"} of treated water held at the tap if the plant / feed stops.`}
          testid="ss-result-reserve"
        />
        <Result
          label="Supply status"
          primary={{
            value: plantIsBottleneck    ? "Plant undersized"
                 : sourceIsBottleneck   ? "Source below demand"
                 : `${fmt((cleanSupply / dailyTotal_L), 2)}×`,
            unit: plantIsBottleneck || sourceIsBottleneck ? "" : "plant margin",
          }}
          status={supplyStatus}
          note={supplyNote}
          testid="ss-result-margin"
        />
      </div>

      {/* Tank sizing panel — clean tank (plant failure) + raw tank (source failure). */}
      <div
        className="mt-5 p-5 md:p-6"
        style={{ background: "#FCFCFA", border: "1px solid rgba(12,42,64,0.08)" }}
      >
        <div
          className="font-mono uppercase mb-3"
          style={{ fontSize: "0.6rem", letterSpacing: "0.22em", color: "rgba(12,42,64,0.6)" }}
        >
          Tank sizing
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <Result
            label="Clean water tank"
            primary={{ value: fmt(cleanReserve_L, 0), unit: "L" }}
            alts={[
              { value: fmt(fromBase("volume", cleanReserve_L, "kL"), 1), unit: "kL" },
              { value: fmt(fromBase("volume", cleanReserve_L, "m3"), 1), unit: "m³" },
            ]}
            note={`Daily demand × ${fmt(cleanDays, 1)} clean day${cleanDays === 1 ? "" : "s"} — treated water at the tap.`}
            testid="ss-result-clean"
          />
          <Result
            label="Raw water tank"
            primary={{ value: fmt(rawReserve_L, 0), unit: "L" }}
            alts={[
              { value: fmt(fromBase("volume", rawReserve_L, "kL"), 1), unit: "kL" },
              { value: fmt(fromBase("volume", rawReserve_L, "m3"), 1), unit: "m³" },
            ]}
            note={`Daily demand × ${fmt(rawDays, 1)} raw day${rawDays === 1 ? "" : "s"} — raw water to keep the plant fed if the source stops.`}
            testid="ss-result-raw"
          />
        </div>
        <div
          className="mt-4 pt-3 flex justify-between"
          style={{ borderTop: "1px dashed rgba(12,42,64,0.15)" }}
        >
          <span className="font-mono uppercase" style={{ fontSize: "0.6rem", letterSpacing: "0.2em", color: "rgba(12,42,64,0.55)" }}>
            Total site storage
          </span>
          <span style={{ fontSize: "0.95rem", fontWeight: 700, color: "#0C2A40" }}>
            {fmt(totalStorage_L, 0)} L
            <span className="ml-2 font-mono" style={{ fontSize: "0.72rem", color: "rgba(12,42,64,0.55)", fontWeight: 500 }}>
              = {fmt(totalStorage_L / 1000, 1)} kL · {fmt(totalDays, 1)} combined day{totalDays === 1 ? "" : "s"}
            </span>
          </span>
        </div>
      </div>

      <SpecCard
        eyebrow="Storage specification"
        testid="ss-spec-card"
        footer={supplyNote}
      >
        {demandMode === "direct" ? (
          <>
            This site needs <SpecValue>{fmt(dailyTotal_L, 0)} L/day</SpecValue> of treated water
            (direct entry{includeIrrigation ? ` + ${fmt(irrDaily_L, 0)} L/day irrigation` : ""}).
            Specify <SpecValue>{fmt(cleanReserve_L / 1000, 1)} kL</SpecValue> of clean water storage
            (<SpecValue>{fmt(cleanDays, 1)}-day</SpecValue> buffer if the plant / feed fails) and{" "}
            <SpecValue>{fmt(rawReserve_L / 1000, 1)} kL</SpecValue> of raw water storage
            (<SpecValue>{fmt(rawDays, 1)}-day</SpecValue> buffer if the source fails). Total site storage:{" "}
            <SpecValue>{fmt(totalStorage_L / 1000, 1)} kL</SpecValue>.
            {sourceIsBottleneck && (
              <>
                {" "}Raw source at <SpecValue>{fmt(rawSupply, 0)} L/day</SpecValue> is below demand — steady-state clean production is capped at the source rate; the raw buffer covers short source interruptions only.
              </>
            )}
            {plantIsBottleneck && (
              <>
                {" "}<SpecValue>Plant capacity {fmt(cleanSupply, 0)} L/day is below demand</SpecValue> — resize the treatment train before finalising storage.
              </>
            )}
          </>
        ) : (
          <>
            A {PROFILES.find((p) => p.value === profile)?.label?.toLowerCase() ?? "site"} with{" "}
            <SpecValue>{fullTimePersons} full-time</SpecValue> and{" "}
            <SpecValue>{partTimePersons} part-time</SpecValue> persons at{" "}
            <SpecValue>{occupancyPct} %</SpecValue> occupancy needs{" "}
            <SpecValue>{fmt(dailyTotal_L, 0)} L/day</SpecValue>. Specify{" "}
            <SpecValue>{fmt(cleanReserve_L / 1000, 1)} kL</SpecValue> of clean water storage
            (<SpecValue>{fmt(cleanDays, 1)}-day</SpecValue> buffer if the plant / feed fails) and{" "}
            <SpecValue>{fmt(rawReserve_L / 1000, 1)} kL</SpecValue> of raw water storage
            (<SpecValue>{fmt(rawDays, 1)}-day</SpecValue> buffer if the source fails). Total site storage:{" "}
            <SpecValue>{fmt(totalStorage_L / 1000, 1)} kL</SpecValue>.
            {sourceIsBottleneck && (
              <>
                {" "}Raw source at <SpecValue>{fmt(rawSupply, 0)} L/day</SpecValue> is below demand — steady-state clean production is capped at the source rate; the raw buffer covers short source interruptions only.
              </>
            )}
            {plantIsBottleneck && (
              <>
                {" "}<SpecValue>Plant capacity {fmt(cleanSupply, 0)} L/day is below demand</SpecValue> — resize the treatment train before finalising storage.
              </>
            )}
          </>
        )}
      </SpecCard>
    </ToolShell>
  );
}

// Plain number input with a fixed text suffix (no unit selector).
function PlainSuffix({ value, onChange, suffix, testid }) {
  // Buffered text-mode input — see SuffixNum (PurificationPlantSizing.jsx)
  // for the full explanation of the two bugs this fixes (can't clear /
  // leading-zero on type).
  const numeric = Number(value);
  const externalStr = Number.isFinite(numeric) && numeric !== 0 ? String(numeric) : "";
  const [buf, setBuf] = React.useState(externalStr);
  React.useEffect(() => {
    const parsed = parseFloat(buf.replace(",", "."));
    const same = Number.isFinite(parsed)
      ? Math.abs(parsed - numeric) < 1e-9
      : (buf === "" && numeric === 0);
    if (!same) setBuf(externalStr);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return (
    <div className="flex items-stretch">
      <input
        type="text"
        inputMode="decimal"
        value={buf}
        placeholder="0"
        data-testid={testid}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw !== "" && !/^-?\d*[.,]?\d*$/.test(raw)) return;
          setBuf(raw);
          if (raw === "" || raw === "-" || raw === "." || raw === ",") { onChange(0); return; }
          const n = parseFloat(raw.replace(",", "."));
          if (Number.isFinite(n)) onChange(n);
        }}
        className="flex-1 min-w-0 px-3 py-2.5 bg-white border outline-none focus:border-[#1FB6C1]"
        style={{ borderColor: "rgba(12,42,64,0.16)", color: "#0C2A40", fontSize: "0.95rem", fontWeight: 500 }}
      />
      <span
        className="inline-flex items-center px-3 border border-l-0 font-mono"
        style={{
          background: "#FAFAF7", borderColor: "rgba(12,42,64,0.16)",
          color: "rgba(12,42,64,0.65)", fontSize: "0.72rem", letterSpacing: "0.04em",
        }}
      >
        {suffix}
      </span>
    </div>
  );
}
